import React from "react";
import { useNavigate } from "react-router-dom";
import { useLogout, useGetIdentity, useOne } from "@refinedev/core";
import {
  Avatar,
  Dropdown,
  Button,
  Space,
  Typography,
  Spin,
} from "antd";
import {
  QuestionCircleOutlined,
  SettingOutlined,
  LogoutOutlined,
  CreditCardOutlined,
  SearchOutlined,
  ReadOutlined,
  CustomerServiceOutlined,
  MessageOutlined,
} from "@ant-design/icons";
import { useSupport } from "../../contexts/support/SupportContext";
import { BuildInfoDisplay } from "@components/build-info";
import { NotificationsPanel } from "@components/notifications/NotificationsPanel";

const { Text } = Typography;

interface TopNavProps {
  collapsed?: boolean;
}

export const TopNav: React.FC<TopNavProps> = ({ collapsed }) => {
  const navigate = useNavigate();
  const { data: identity } = useGetIdentity();
  const { mutate: logout, isLoading: isLoggingOut } = useLogout();
  const user = identity as any;
  const { openSupport } = useSupport();
  const { data: profileResult } = useOne({
    resource: "profile",
    id: "current",
    meta: { endpoint: "profile" },
  });
  const profile = (profileResult?.data || {}) as any;
  const profileName = [profile.firstName, profile.lastName].filter(Boolean).join(" ").trim();
  const displayName = profileName || profile.businessName || user?.given_name || user?.email || "User";
  const showEmail = Boolean(user?.email) && displayName !== user?.email;

  const userMenuItems = [
    {
      key: "settings",
      icon: <SettingOutlined />,
      label: "Settings",
      onClick: () => navigate("/settings"),
    },
    {
      key: "subscription",
      icon: <CreditCardOutlined />,
      label: "Subscription & Billing",
      onClick: () => navigate("/subscription"),
    },
    {
      type: "divider" as const,
    },
    {
      key: "logout",
      icon: isLoggingOut ? <Spin size="small" /> : <LogoutOutlined />,
      label: isLoggingOut ? "Signing out..." : "Sign Out",
      onClick: () => logout(),
      disabled: isLoggingOut,
      danger: true,
    },
  ];

  const getUserInitials = () => {
    const source = profileName || user?.given_name || user?.email || "";
    const parts = source.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    if (source) return source[0].toUpperCase();
    return "U";
  };

  return (
    <div
      style={{
        background: "white",
        borderBottom: "1px solid #e5e7eb",
        height: "64px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 24px",
        position: "sticky",
        top: 0,
        zIndex: 99,
        transition: "all 0.3s",
      }}
    >
      {/* Left side - Search (placeholder for future) */}
      <div style={{ flex: 1, maxWidth: "400px" }}>
        <Button
          aria-label="Search"
          icon={<SearchOutlined />}
          style={{
            width: "100%",
            textAlign: "left",
            color: "#6b7280",
            border: "1px solid #e5e7eb",
            background: "#f9fafb",
          }}
        >
          <span style={{ color: "#9ca3af" }}>Search...</span>
          <span
            style={{
              float: "right",
              fontSize: "11px",
              color: "#9ca3af",
              marginLeft: "auto",
            }}
          >
            ⌘K
          </span>
        </Button>
      </div>

      {/* Right side - Actions and User */}
      <Space size="middle">
        {/* Environment Badge */}
        <BuildInfoDisplay
          variant="badge"
          style={{
            fontSize: "11px",
            padding: "2px 8px",
            background: "#f3f4f6",
            color: "#6b7280",
            borderRadius: "6px",
            fontFamily: "monospace",
          }}
        />

        <Dropdown
          menu={{
            items: [
              { key: "docs", icon: <ReadOutlined />, label: "Help & Docs", onClick: () => navigate("/docs") },
              { key: "support", icon: <CustomerServiceOutlined />, label: "Contact support", onClick: () => openSupport() },
              { key: "cases", icon: <MessageOutlined />, label: "My cases", onClick: () => navigate("/support") },
            ],
          }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button
            type="text"
            aria-label="Help"
            icon={<QuestionCircleOutlined />}
            style={{
              color: "#6b7280",
              fontSize: "16px",
            }}
          />
        </Dropdown>

        {/* Notifications */}
        <NotificationsPanel />

        {/* Settings */}
        <Button
          type="text"
          aria-label="Settings"
          icon={<SettingOutlined />}
          style={{
            color: "#6b7280",
            fontSize: "16px",
          }}
          onClick={() => navigate("/settings")}
        />

        {/* User Menu */}
        <Dropdown
          menu={{ items: userMenuItems }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              cursor: "pointer",
              padding: "6px 12px",
              borderRadius: "8px",
              transition: "background 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#f9fafb";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
          >
            <Avatar
              size={32}
              style={{
                background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                color: "white",
                fontWeight: 600,
                fontSize: "13px",
              }}
            >
              {getUserInitials()}
            </Avatar>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <Text
                style={{
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#111827",
                  lineHeight: "1.2",
                }}
              >
                {displayName}
              </Text>
              {showEmail && (
                <Text
                  style={{
                    fontSize: "12px",
                    color: "#6b7280",
                    lineHeight: "1.2",
                  }}
                >
                  {user.email}
                </Text>
              )}
            </div>
          </div>
        </Dropdown>
      </Space>
    </div>
  );
};

